/**
 * Build a human-readable label for conditional logic conditions.
 * e.g. "Employment Status = Employed"
 */
export function buildConditionLabel(conditions, steps) {
    if (!conditions || conditions.length === 0) return '';
    return conditions.map((condition) => {
        const sourceStep = steps.find(s => s.id === condition.source_step_id);
        const sourceField = sourceStep?.fields?.find(f => f.id === condition.source_field_id);
        const fieldName = sourceField?.name || sourceField?.field_label || `Field #${condition.source_field_id}`;
        const operatorLabel = {
            equals: '=', not_equals: '!=',
            in: 'in', not_in: 'not in',
            is_empty: 'is empty', is_not_empty: 'is not empty'
        }[condition.operator] || condition.operator;
        const needsValue = condition.operator !== 'is_empty' && condition.operator !== 'is_not_empty';
        if (needsValue && condition.value != null) {
            const options = getFieldOptions(sourceField);
            const labelFor = (v) => options.find(o => o.value === v)?.label ?? v;
            const valueLabel = Array.isArray(condition.value)
                ? condition.value.map(labelFor).join(', ')
                : labelFor(condition.value);
            return `${fieldName} ${operatorLabel} "${valueLabel}"`;
        }
        return `${fieldName} ${operatorLabel}`;
    }).join(' AND ');
}

// Extract the selectable options for a field, if it defines any.
function getFieldOptions(field) {
    if (!field?.validation_rules) return [];
    if (field.field_type === 'BOOLEAN') {
        return [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }];
    }
    try {
        const rules = typeof field.validation_rules === 'string'
            ? JSON.parse(field.validation_rules)
            : field.validation_rules;
        return rules?.options || [];
    } catch {
        return [];
    }
}

/**
 * Resolve an auto-outcome rule's action, normalising the legacy shape
 * (outcome only) to one of 'complete' | 'close' | 'route'.
 */
export function getAutoOutcomeAction(rule) {
    return rule.action || (rule.outcome === 'negative' ? 'close' : 'complete');
}

/**
 * Short label for a single auto-outcome rule's action.
 * Handles the legacy shape (outcome only) and the new "route" action.
 */
export function buildAutoOutcomeRuleLabel(rule, steps) {
    const action = getAutoOutcomeAction(rule);
    if (action === 'route') {
        const target = (steps || []).find(st => st.id === rule.target_step_id);
        if (!target) return 'Go to step';
        return `Go to Step ${(target.position ?? 0) + 1}: ${target.name}`;
    }
    return action === 'close' ? 'Close' : 'Complete';
}

/**
 * Build short label and full detail for auto-outcome rules.
 */
export function buildAutoOutcomeInfo(autoOutcomeRules, steps) {
    if (!autoOutcomeRules || autoOutcomeRules.length === 0) return { label: null, detail: null };
    const label = 'Auto: ' + autoOutcomeRules.map(r => buildAutoOutcomeRuleLabel(r, steps)).join(', ');
    const detail = autoOutcomeRules.map(rule => {
        const conds = (rule.conditions || []).map(c => {
            const s = steps.find(st => st.id === c.source_step_id);
            const f = s?.fields?.find(fl => fl.id === c.source_field_id);
            const val = c.value != null ? ` "${c.value}"` : '';
            return `${f?.name || 'field'} ${c.operator}${val}`;
        }).join(' & ');
        return conds
            ? `${buildAutoOutcomeRuleLabel(rule, steps)} when ${conds}`
            : `${buildAutoOutcomeRuleLabel(rule, steps)} on completion`;
    }).join(' | ');
    return { label, detail };
}

/**
 * Compute the new ordered step IDs after moving a step to a new position.
 */
export function computeReorderedStepIds(steps, fromStepId, direction) {
    const sorted = [...steps].sort((a, b) => a.position - b.position);
    const fromIndex = sorted.findIndex(s => s.id === fromStepId);
    if (fromIndex === -1) return sorted.map(s => s.id);

    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= sorted.length) return sorted.map(s => s.id);

    const newOrder = [...sorted];
    [newOrder[fromIndex], newOrder[toIndex]] = [newOrder[toIndex], newOrder[fromIndex]];
    return newOrder.map(s => s.id);
}
